/**
 * EncuestaResults - Resultados agregados de una instancia de encuesta
 * Features:
 * - Nivel ético promedio
 * - Participación
 * - Distribución de respuestas
 * - Matriz de cualificación
 * - Gráficas de resultados
 */
import { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  CheckCircle,
  MinusCircle,
  XCircle,
  FileText,
  Download,
  BarChart3,
} from 'lucide-react';
import { encuestasAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import CualificacionMatrix from './CualificacionMatrix';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORES_RESPUESTAS = {
  si: '#10b981', // green-500
  parcialmente: '#f59e0b', // yellow-500
  no: '#ef4444', // red-500
};

const COLORES_NIVELES = {
  Alto: '#10b981',
  Medio: '#f59e0b',
  Bajo: '#fb923c',
  Crítico: '#ef4444',
};

const EncuestaResults = ({ instanciaId }) => {
  const [analisis, setAnalisis] = useState(null);
  const [participacion, setParticipacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResultados();
  }, [instanciaId]);

  const loadResultados = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar análisis y participación en paralelo
      const [analisisRes, participacionRes] = await Promise.all([
        encuestasAPI.getAnalisis(instanciaId),
        encuestasAPI.getParticipacion(instanciaId),
      ]);

      setAnalisis(analisisRes.data);
      setParticipacion(participacionRes.data);
    } catch (err) {
      console.error('Error al cargar resultados:', err);
      setError(err.message || 'Error al cargar resultados');
    } finally {
      setLoading(false);
    }
  };

  const getNivelEticoInfo = (nivel) => {
    const niveles = {
      Alto: {
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
        icon: '🟢',
        descripcion: 'Nivel ético excelente',
      },
      Medio: {
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-500',
        icon: '🟡',
        descripcion: 'Nivel ético aceptable con mejoras',
      },
      Bajo: {
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        borderColor: 'border-orange-500',
        icon: '🟠',
        descripcion: 'Nivel ético requiere atención',
      },
      Crítico: {
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-500',
        icon: '🔴',
        descripcion: 'Nivel ético crítico - acción urgente',
      },
    };

    return niveles[nivel] || niveles.Medio;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando resultados..." />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" title="Error" message={error} />;
  }

  if (!analisis || !participacion) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No se pudieron cargar los resultados</p>
      </div>
    );
  }

  const nivelInfo = getNivelEticoInfo(analisis.nivel_etico_promedio);
  const porcentajeParticipacion = participacion.total_participantes > 0
    ? Math.round((participacion.participantes_completados / participacion.total_participantes) * 100)
    : 0;

  // Preparar datos para gráficas
  const dataPorPregunta = analisis.distribucion_por_pregunta || [];
  
  // Gráfica de barras: distribución de respuestas por pregunta
  const chartDataPreguntas = dataPorPregunta.slice(0, 12).map((p, index) => ({
    name: `P${index + 1}`,
    'Sí': p.respuestas_si || 0,
    'Parcialmente': p.respuestas_parcialmente || 0,
    'No': p.respuestas_no || 0,
  }));

  // Gráfica de pie: distribución de niveles éticos
  const distribucionNiveles = analisis.distribucion_niveles || {};
  const chartDataNiveles = Object.keys(distribucionNiveles).map((nivel) => ({
    name: nivel,
    value: distribucionNiveles[nivel],
  }));

  // Datos para matriz de cualificación
  const matrixData = {
    puntuacion_promedio: analisis.puntuacion_promedio || 0,
    nivel_etico: analisis.nivel_etico_promedio,
    distribucion: distribucionNiveles,
  };

  return (
    <div className="space-y-6">
      {/* Indicador principal */}
      <div
        className={`${nivelInfo.bgColor} ${nivelInfo.borderColor} border-l-4 p-6 rounded-r-lg`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{nivelInfo.icon}</span>
            <div>
              <h3 className={`text-2xl font-bold ${nivelInfo.textColor}`}>
                NIVEL ÉTICO: {analisis.nivel_etico_promedio.toUpperCase()}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {nivelInfo.descripcion}
              </p>
            </div>
          </div>
        </div>

        {/* Métricas principales */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">
                Puntuación Promedio
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {analisis.puntuacion_promedio?.toFixed(1) || 0}/12
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {Math.round((analisis.puntuacion_promedio / 12) * 100)}% de cumplimiento
            </p>
          </div>

          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">
                Participación
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {participacion.participantes_completados}/{participacion.total_participantes}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {porcentajeParticipacion}% completado
            </p>
          </div>

          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">
                Total Respuestas
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {analisis.total_respuestas || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Matriz de Cualificación */}
      <CualificacionMatrix data={matrixData} />

      {/* Distribución de niveles éticos */}
      {chartDataNiveles.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Distribución de Niveles Éticos
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gráfica de pie */}
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartDataNiveles}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartDataNiveles.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORES_NIVELES[entry.name]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Lista de niveles */}
            <div className="space-y-3">
              {Object.keys(distribucionNiveles).map((nivel) => {
                const info = getNivelEticoInfo(nivel);
                const cantidad = distribucionNiveles[nivel];
                const porcentaje = participacion.participantes_completados > 0
                  ? Math.round((cantidad / participacion.participantes_completados) * 100)
                  : 0;

                return (
                  <div
                    key={nivel}
                    className={`${info.bgColor} border ${info.borderColor} rounded-lg p-4`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{info.icon}</span>
                        <div>
                          <p className={`font-semibold ${info.textColor}`}>
                            {nivel}
                          </p>
                          <p className="text-sm text-gray-600">
                            {cantidad} participante{cantidad !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          {porcentaje}%
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Distribución de respuestas por pregunta */}
      {chartDataPreguntas.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Distribución de Respuestas por Pregunta
          </h4>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartDataPreguntas}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Sí" fill={COLORES_RESPUESTAS.si} />
              <Bar dataKey="Parcialmente" fill={COLORES_RESPUESTAS.parcialmente} />
              <Bar dataKey="No" fill={COLORES_RESPUESTAS.no} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Resumen de respuestas */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Resumen de Respuestas
        </h4>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Respuestas "Sí"
              </span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {analisis.total_si || 0}
            </p>
            <p className="text-xs text-green-600 mt-1">
              {analisis.total_respuestas > 0
                ? Math.round((analisis.total_si / analisis.total_respuestas) * 100)
                : 0}%
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MinusCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">
                Respuestas "Parcialmente"
              </span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">
              {analisis.total_parcialmente || 0}
            </p>
            <p className="text-xs text-yellow-600 mt-1">
              {analisis.total_respuestas > 0
                ? Math.round((analisis.total_parcialmente / analisis.total_respuestas) * 100)
                : 0}%
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                Respuestas "No"
              </span>
            </div>
            <p className="text-2xl font-bold text-red-700">
              {analisis.total_no || 0}
            </p>
            <p className="text-xs text-red-600 mt-1">
              {analisis.total_respuestas > 0
                ? Math.round((analisis.total_no / analisis.total_respuestas) * 100)
                : 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button
          variant="outline"
          icon={Download}
          onClick={() => {
            alert('Función de descarga en desarrollo');
          }}
        >
          Descargar Reporte
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
    </div>
  );
};

export default EncuestaResults;
